import React, { useMemo, useState } from "react";
import { FaChevronDown, FaSearch } from "react-icons/fa";
import "./faq.css";

const faqData = [
  {
    category: "ORDERS",
    question: "How can I place an order?",
    answer:
      "Choose your preferred timepiece, open its product page, select your quantity and proceed with Buy Now or Add to Cart. Enter your delivery details and select your preferred payment method to complete the order.",
  },
  {
    category: "ORDERS",
    question: "Can I cancel my order after placing it?",
    answer:
      "If your order has not yet been processed, you may contact our support team as soon as possible to request cancellation. Once an order has entered processing or dispatch, cancellation may no longer be possible.",
  },
  {
    category: "ORDERS",
    question: "How will I know that my order has been placed successfully?",
    answer:
      "After successfully completing your order, you will see an order confirmation on the website. Our team will also process your order using the contact information provided during checkout.",
  },

  {
    category: "PAYMENT",
    question: "What payment methods do you accept?",
    answer:
      "Time Valley currently offers Cash on Delivery and JazzCash payment options. Available payment methods are displayed during the checkout process.",
  },
  {
    category: "PAYMENT",
    question: "How does JazzCash payment work?",
    answer:
      "Select JazzCash during checkout and follow the payment instructions displayed on the screen. After making the payment, upload your payment proof where requested so our team can verify the transaction.",
  },
  {
    category: "PAYMENT",
    question: "Is my payment information secure?",
    answer:
      "Time Valley takes reasonable measures to protect information submitted through the website. We recommend keeping your payment credentials and personal information private.",
  },

  {
    category: "DELIVERY",
    question: "Do you offer Cash on Delivery?",
    answer:
      "Yes. Cash on Delivery is available for eligible orders. Simply select Cash on Delivery during checkout and complete your order details.",
  },
  {
    category: "DELIVERY",
    question: "How long does delivery take?",
    answer:
      "Delivery time can vary depending on your location and order processing. Our team will process your order after confirmation and arrange delivery to the address provided.",
  },
  {
    category: "DELIVERY",
    question: "Can I change my delivery address?",
    answer:
      "If your order has not yet been dispatched, contact Time Valley as soon as possible with your order details and the corrected delivery address.",
  },

  {
    category: "WATCHES",
    question: "Are Time Valley watches suitable for everyday wear?",
    answer:
      "Our collection is designed around versatile styling, refined details and everyday wearability. Each product page provides specific information about the individual watch.",
  },
  {
    category: "WATCHES",
    question: "Where can I find the specifications of a watch?",
    answer:
      "Detailed specifications such as movement, case, glass and water resistance are available on the individual product page of each watch.",
  },
  {
    category: "WATCHES",
    question: "Are the watches water resistant?",
    answer:
      "Water resistance varies by model. Please check the specifications section on the product page of the specific timepiece before exposing it to water.",
  },

  {
    category: "SUPPORT",
    question: "How can I contact Time Valley?",
    answer:
      "You can contact the Time Valley team through the contact information provided on our website. For an order-related query, keep your order details ready so we can assist you more efficiently.",
  },
  {
    category: "SUPPORT",
    question: "What should I do if I receive a damaged product?",
    answer:
      "Please contact Time Valley as soon as possible after receiving the package and provide your order details along with clear photographs of the product and packaging.",
  },
];

const categories = ["ALL", "ORDERS", "PAYMENT", "DELIVERY", "WATCHES", "SUPPORT"];

function FAQ() {
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [activeIndex, setActiveIndex] = useState(null);
  const [search, setSearch] = useState("");

  const filteredFAQs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory =
        activeCategory === "ALL" || item.category === activeCategory;

      const searchText = search.toLowerCase().trim();

      const matchesSearch =
        !searchText ||
        item.question.toLowerCase().includes(searchText) ||
        item.answer.toLowerCase().includes(searchText);

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, search]);

  const handleCategory = (category) => {
    setActiveCategory(category);
    setActiveIndex(null);
  };

  const toggleFAQ = (index) => {
    setActiveIndex((current) => (current === index ? null : index));
  };

  return (
    <main className="tv-faq-page">

      {/* =====================================================
          FAQ HERO
      ===================================================== */}

      <section className="tv-faq-hero">

        <div className="tv-faq-hero-glow"></div>

        <div className="tv-faq-hero-content">

          <span className="tv-faq-eyebrow">
            THE TIME VALLEY JOURNAL
          </span>

          <h1>
            Frequently Asked
            <span> Questions</span>
          </h1>

          <p>
            Everything you need to know about our timepieces,
            orders, payments and delivery.
          </p>

        </div>

        <div className="tv-faq-hero-line"></div>

      </section>


      {/* =====================================================
          SEARCH
      ===================================================== */}

      <section className="tv-faq-search-section">

        <div className="tv-faq-search-box">

          <FaSearch />

          <input
            type="text"
            placeholder="Search your question..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setActiveIndex(null);
            }}
          />

        </div>

      </section>


      {/* =====================================================
          FAQ CONTENT
      ===================================================== */}

      <section className="tv-faq-main">

        <div className="tv-faq-category-bar">

          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={
                activeCategory === category
                  ? "tv-faq-category tv-faq-category-active"
                  : "tv-faq-category"
              }
              onClick={() => handleCategory(category)}
            >
              {category}
            </button>
          ))}

        </div>


        <div className="tv-faq-heading">

          <div>
            <span>NEED TO KNOW</span>

            <h2>
              Answers, before
              <em> you ask.</em>
            </h2>
          </div>

          <p>
            Find answers to the most common questions about
            the Time Valley experience.
          </p>

        </div>


        <div className="tv-faq-list">

          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((item, index) => {

              const isOpen = activeIndex === index;

              return (
                <div
                  className={
                    isOpen
                      ? "tv-faq-item tv-faq-item-open"
                      : "tv-faq-item"
                  }
                  key={`${item.category}-${item.question}`}
                >

                  <button
                    type="button"
                    className="tv-faq-question"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={isOpen}
                  >

                    <div className="tv-faq-question-left">

                      <span className="tv-faq-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <div>

                        <small>{item.category}</small>

                        <strong>
                          {item.question}
                        </strong>

                      </div>

                    </div>

                    <span className="tv-faq-icon">
                      <FaChevronDown />
                    </span>

                  </button>


                  <div
                    className="tv-faq-answer-wrap"
                    style={{
                      maxHeight: isOpen ? "300px" : "0px",
                    }}
                  >

                    <div className="tv-faq-answer">

                      <div className="tv-faq-answer-line"></div>

                      <p>
                        {item.answer}
                      </p>

                    </div>

                  </div>

                </div>
              );
            })
          ) : (
            <div className="tv-faq-empty">

              <span>NO RESULTS</span>

              <h3>
                We couldn't find that answer.
              </h3>

              <p>
                Try another search term or explore one of the
                categories above.
              </p>

            </div>
          )}

        </div>

      </section>


      {/* =====================================================
          BOTTOM CTA
      ===================================================== */}

      <section className="tv-faq-cta">

        <div className="tv-faq-cta-inner">

          <span>STILL HAVE A QUESTION?</span>

          <h2>
            Let us help you find
            <em> your answer.</em>
          </h2>

          <p>
            Our team is here to assist you with your Time Valley
            experience.
          </p>

          <a href="/contact" className="tv-faq-cta-button">
            CONTACT TIME VALLEY
          </a>

        </div>

      </section>

    </main>
  );
}

export default FAQ;